import {useState, useEffect} from "react"
import {useQuery} from "react-query"
import nprogress from "nprogress"
import {toast} from "react-toastify"
import swal from "sweetalert"
import useAuth from "auth/useAuth"
import {isEmpty, setLocalItem, errorMessage} from "utils/utils"
import {isEqual} from "lodash"
import {saveAs} from "file-saver"


const EMPTY_PERMISSIONS = {}
const NEW_PERMISSIONS = {
    submit: true,
    unsubmit: false,
    save: true,
    edit: true,
    forward: true,
    download: false
}

export default function useRescue(id) {

    const rescueKey = 'rescue-' + (id ?? 'new')
    const baseKey = 'base-' + (id ?? 'new')
    const dirtyKey = 'dirty-' + (id ?? 'new')

    const {auth, API, username} = useAuth()
    const [rescue, setRescue] = useState({})
    const [baseRescue, setBaseRescue] = useState(() => {
        const stored = localStorage.getItem(baseKey)
        return stored ? JSON.parse(stored) : {}
    })
    const [permissions, setPermissions] = useState(!id ? NEW_PERMISSIONS : EMPTY_PERMISSIONS)
    const [ready, setReady] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [userEdited, setUserEdited] = useState(localStorage.getItem(dirtyKey) ?? false)
    const submitted = !!rescue.submitted
    const isReadOnly = !permissions?.save
    const hasId = !!id

    // Compute dirty by comparing rescue to base, ignoring auto-populated fields
    const computeDirty = () => {
        if (!userEdited) return false
        // Compare meaningful fields (exclude auto-populated: location, area, events)
        const meaningful = (r) => ({
            subject: r.subject,
            injury: r.injury,
            rescuers: r.rescuers,
            description: r.description,
            weather: r.weather,
            aid: r.aid,
            end: r.end,
            resort: r.resort
        })
        return !isEqual(meaningful(rescue), meaningful(baseRescue))
    }
    const dirty = computeDirty()
    
    
    const restore = () => {
        const rescue = localStorage.getItem(rescueKey)
        return JSON.parse(rescue)
    }
    
    const init = () => {
        return {
            rescue: {username},
            permissions: NEW_PERMISSIONS
        }
    }
    
    const query = useQuery(["rescue", id], () => {
        if (rescueKey in localStorage) return restore()
        if (!id) return init()
        return auth(API.rescuesIdGet, {id}).then(response => response.data)
    })
        
        
    useEffect(() => {
        if (ready || !query.isFetched) return
        setReady(true)
        const initialRescue = query.data?.rescue ?? {}
        setRescue(initialRescue)
        // Only set baseRescue if not restored from localStorage (i.e., base wasn't already stored)
        if (!localStorage.getItem(baseKey)) {
            setBaseRescue(initialRescue)
            setLocalItem(baseKey, initialRescue)
        }
        setPermissions(query.data?.permissions ?? EMPTY_PERMISSIONS)
    }, [query.data, query.isFetched])


    useEffect(() => {
        if (!ready) return
        if (isEmpty(rescue) || isReadOnly) {
            clear()
            return
        }

        // Only save to localStorage if there are meaningful changes
        if (computeDirty()) {
            const obj = {rescue, permissions}
            setLocalItem(rescueKey, obj)
        }
    }, [rescue])


    useEffect(() => {
        if (userEdited) setLocalItem(dirtyKey, true)
        else localStorage.removeItem(dirtyKey)
    }, [userEdited])
    
        
    const save = (onSuccess=null, onError=null) => {
        if (isSaving) return
        setIsSaving(true)
        if (!onSuccess) nprogress.start()
        auth(API.rescuesPost, null, rescue).then(response => {
            setIsSaving(false)
            setRescue(response.data.rescue)
            setPermissions(response.data.permissions)
            reload()
            if (onSuccess) onSuccess(response)
            else nprogress.done()
        }).catch(response => {
            setIsSaving(false)
            if (onError) onError(response)
            else {
                nprogress.done()
                swal("Greška", errorMessage(response), "error")
            }
        })
    }
    
    const clear = () => {
        localStorage.removeItem(rescueKey)
        localStorage.removeItem(baseKey)
        localStorage.removeItem(dirtyKey)
        setUserEdited(false)
        setBaseRescue(rescue)
    }
    
    const reload = () => {
        clear()
        setReady(false)
        query.refetch()
    }
    
    const erase = (onSuccess=null, onError=null) => {
        if (!rescue.id) return
        if (!onSuccess) nprogress.start()
        auth(API.rescuesIdDelete, {id: rescue.id}).then(response => {
            clear()
            if (onSuccess) onSuccess(response)
            else nprogress.done()
        }).catch(response => {
            if (onError) onError(response)
            else {
                nprogress.done()
                swal("Greška", errorMessage(response), "error")
            }
        })
    }
    
    const submit = (onSuccess=null, onError=null) => {
        if (!onSuccess) nprogress.start()
        console.log("SUBMIT", id, rescue.id)
        auth(API.rescuesIdSubmitPost, {id: rescue.id}).then(response => {            
            reload()
            if (onSuccess) onSuccess(response)
            else nprogress.done()
        }).catch(response => {
            if (onError) onError(response)
            else {
                nprogress.done()
                console.log("SUBMIT ERROR", response)
                swal("Greška", errorMessage(response), "error")
            }
        })
    }
    
    const unsubmit = (onSuccess) => {
        nprogress.start()
        auth(API.rescuesIdSubmitDelete, {id: rescue.id}).then(response => {
            nprogress.done()
            setReady(false)
            query.refetch()
            if (onSuccess) onSuccess()
        }).catch(response => {
            nprogress.done()
            swal("Greška", errorMessage(response), "error")
        })
    }
    
    
    const setField = (id, value) => {
        if (isReadOnly) return
        let updated = false
        const parts = id.split("/")
        const resc = {...rescue}
        var obj = resc
        for (var i = 0; i < parts.length-1; i++) {
            const part = parts[i]
            if (!(part in obj)) {
                updated = true
                obj[part] = {}
            }
            obj = obj[part]
        }
        if (!isEqual(obj?.[parts[parts.length-1]], value)) {
            obj[parts[parts.length-1]] = value
            updated = true
        }
        if (updated) {
            setRescue(resc)
            setUserEdited(true)
        }
    }


    const setFields = (pairs) => {
        if (isReadOnly) return
        let updated = false
        const resc = {...rescue}
        for (const id in pairs) {
            const value = pairs[id]
            const parts = id.split("/")
            var obj = resc
            for (var i = 0; i < parts.length-1; i++) {
                const part = parts[i]
                if (!(part in obj)) {
                    updated = true
                    obj[part] = {}
                }
                obj = obj[part]
            }
            if (!isEqual(obj?.[parts[parts.length-1]], value)) {
                obj[parts[parts.length-1]] = value
                updated = true
            }
        }
        if (updated) {
            setRescue(resc)
            setUserEdited(true)
        }
    }    
    
    
    const download = (onSuccess=null, onError=null) => {
        if (!onSuccess) nprogress.start()
        auth(API.rescuesIdReportGet, {id}).then(response => {
            // Unmapped Lambda errors arrive as HTTP 200 with an errorMessage body
            if (!response.data?.content) throw response
            var byteString = atob(response.data.content)
            var ab = new ArrayBuffer(byteString.length)
            var ia = new Uint8Array(ab)
            for (var i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i)
            }
            var blob = new Blob([ia], { type: "application/pdf" })
            saveAs(blob, response.data.filename)
            if (onSuccess) onSuccess(response)
            else nprogress.done()
        }).catch(response => {
            if (onError) onError(response)
            else {
                nprogress.done()
                swal("Greška", errorMessage(response), "error")
            }
        })
    }

    
    const onChange = (event) => {
        const {id, value} = event.target
        setField(id, value)
    }
    
    const getTime = () => {
        let time = rescue.created
        if (rescue.events && rescue.events.length > 0 && rescue.events[0].time)
            time = rescue.events[0].time
        return time
    }
    
    const getSubjectName = () => {
        var subject = rescue?.subject
        if (subject) {
            if (subject.given_name && subject.family_name) return subject.given_name + ' ' + subject.family_name
            if (subject.given_name) return subject.given_name
            if (subject.family_name) return subject.family_name
        }
        return null
    }


    const containsRescuer = (rescuer) => {
        if (rescue.username === rescuer.username) return true
        for (const resc of rescue.rescuers ?? [])
            if (resc.username === rescuer.username) return true
        return false
    }

    const addRescuer = (rescuer) => {
        if (containsRescuer(rescuer)) return false
        const rescuers = rescue.rescuers ? [...rescue.rescuers] : []
        rescuers.push(rescuer)
        setField("rescuers", rescuers)
        return true
    }
    
    const removeRescuer = (rescuer) => {
        if (!containsRescuer(rescuer)) return false
        const rescuers = rescue.rescuers?.filter(resc => resc.username !== rescuer.username) ?? []
        setField("rescuers", rescuers)
        return true
    }
    
    
    const forward = (toUsername) => {
        const toaster = toast.loading("Prosleđujem...")
        save(response => {
            auth(API.rescuesIdRescuerUsernamePatch, {id: response.data.rescue.id, username: toUsername}).then(response => {
                reload()
                toast.update(toaster, {render: "Prosleđeno", type: "success", isLoading: false, autoClose: 2500})
            }).catch(response => {
                console.log("ERR1", response)
                toast.update(toaster, {render: `Neka greška [1]: ${errorMessage(response)}`, type: "error", isLoading: false, autoClose: 2500})
            })
        }, response => {
            console.log("ERR2", response)
            toast.update(toaster, {render: `Neka greška [2]: ${errorMessage(response)}`, type: "error", isLoading: false, autoClose: 2500})
        })
    }

    const improve = (onSuccess=null, onError=null) => {
        const toaster = toast.loading("Sređujem tekst...")
        auth(API.rescuesImprovePost, null, rescue).then(response => {
            setRescue(response.data)
            setUserEdited(true)
            if (onSuccess) onSuccess(response)
            toast.update(toaster, {render: "Tekst je sređen", type: "success", isLoading: false, autoClose: 2500})
        }).catch(response => {
            if (onError) onError(response)
            else {
                console.log("IMPROVE ERROR", response)
                toast.update(toaster, {render: `Greška: ${errorMessage(response)}`, type: "error", isLoading: false, autoClose: 2500})
            }
        })
    }



    return {...query, save, clear, erase, reload, submit, unsubmit, download,
            setField, setFields, rescue, permissions, ready, dirty, submitted,
            onChange, getSubjectName, isReadOnly, getTime, hasId, isSaving,
            containsRescuer, addRescuer, removeRescuer, forward, improve}

}